<template>
  <StepItem
    format="form"
    :title="store.isGiveaway ? 'Final Step – Account Information' : 'Final Step – Account Information'"
    :subtitle="store.isGiveaway ? 'We keep your info secure - This is how we\'ll contact you if you win.' : 'We keep your information secure, and only share it with employers when required.'"
    show-lock
    @handle-prev-step="handlePrevStep"
    @handle-next-step="handleNextStep"
    can-go-to-prev-step
    :loading="loading"
    :is-ready-for-next-step="isReadyForNextStep">
    <template v-slot:default>
      <FormInput
        class="mb-6 w-full"
        format="phone"
        type="tel"
        required
        label="Phone Number"
        placeholder="Phone number"
        v-model="store.phone"
        :error="store?.errors?.phone"
        @update:modelValue="() => throttleValidateCurrentStep()"
        autocomplete="tel"
      />
      <p v-if="smsError" class="-mt-4 mb-6 text-sm text-green-600">
        Phone number taken? We'd love to help:
        <a class="underline"
           href="mailto:support@businessdraft.com">
          support@businessdraft.com
        </a>
      </p>
      <FormCityLatLng
        class="mb-6 w-full"
        label="Select what US city you're in"
        placeholder="Start typing your city"
        required
        v-model="store.location"
        v-model:lat="store.lat"
        v-model:lng="store.lng"
        v-model:line1="store.addressLine1"
        v-model:line2="store.addressLine2"
        v-model:city="store.city"
        v-model:state="store.state"
        v-model:postal-code="store.postalCode"
        v-model:country="store.country"
        @update:modelValue="() => throttleValidateCurrentStep()"
      />
      <div
        class="flex flex-col items-center justify-between sm:flex-row sm:gap-4 md:gap-6"
      >
        <FormInput
          type="password"
          class="w-full"
          required
          label="Password"
          placeholder="••••••••"
          v-model="store.password"
          :error="store?.errors?.password"
          @update:modelValue="throttleValidateCurrentStep"
          autocomplete="new-password"
        />
        <FormInput
          type="password"
          class="w-full"
          required
          label="Confirm Password"
          placeholder="••••••••"
          autocomplete="new-password"
          v-model="store.passwordConfirmation"
          :error="store?.errors?.passwordConfirmation"
          @update:modelValue="throttleValidateCurrentStep"
        />
      </div>
      <FormPasswordValidator
        :password="store.password"
        :password-confirmation="store.passwordConfirmation"/>
    </template>
    <template v-slot:next-step-text>
      <span v-if="!loading">Create Account <span v-if="store.isGiveaway"> & Enter Now </span>🎉</span>
    </template>
  </StepItem>
</template>

<script setup>
import StepItem from "./StepItem.vue";
import FormCityLatLng from "./FormCityLatLng.vue";
import FormInput from "./FormInput.vue";
import FormPasswordValidator from "./FormPasswordValidator.vue";
import {onActivated, onMounted, ref} from "vue";
import {useCandidateAccountCreateStore} from "../stores/CandidateAccountCreateStore";
import {throttle} from "../helpers/DebounceAndThrottle.js";
import {useAccountStore} from "../stores/AccountStore.js";

const emits = defineEmits(["complete", "prev-step"]);

const accountStore = useAccountStore();
const store = useCandidateAccountCreateStore();
const isReadyForNextStep = ref(false);
const smsError = ref(false);
const loading = ref(false);
const handlePrevStep = () => emits("prev-step", 1);

const handleNextStep = () => {
  if (loading.value) return;
  loading.value = true;

  try {
    validateCurrentStep(true);
    if (isReadyForNextStep.value) emits("complete");
  } catch (e) {
    console.error(e);
  }

  loading.value = false;
};

const validateCurrentStep = (showToast = false) =>
  (isReadyForNextStep.value =
    store.validateStep2(showToast));
const throttleValidateCurrentStep = () =>
  throttle(() => validateCurrentStep(false), 300);

onActivated(() => {
  if (accountStore.isLoggedIn) emits("complete");
});

onMounted(() => {
  if (accountStore.isLoggedIn) return emits("complete");
});
</script>
